import React, { useEffect, useState } from 'react';
import { ExternalLink, Shield, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getIdToken } from '../../services/firebase';
import { fetchSubscriptionDetails } from '../../services/licenseService';
import { getSearchLicenseSummary } from '../../utils/licenseStatus';
import {
  getKiddowareAccountUrl,
  SEARCH_LICENSE_PRODUCT_LABELS,
  SEARCH_LICENSE_UPGRADE_HINT,
} from '../../config/kiddowareUrls';
import { trackUserAction } from '../../utils/analytics';

const cardStyle = {
  padding: 16,
  borderRadius: 14,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  marginBottom: 20,
};

function StatusPill({ active, label }) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 800,
        padding: '4px 10px',
        borderRadius: 999,
        background: active ? 'rgba(34,197,94,0.18)' : 'rgba(148,163,184,0.22)',
        color: 'var(--color-text)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function ProductLicenseRow({ product }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)' }}>{product.name}</div>
        <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>{product.statusLabel}</div>
        {product.expiresLabel ? (
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2 }}>{product.expiresLabel}</div>
        ) : null}
      </div>
      <StatusPill active={product.active} label={product.active ? 'Active' : 'Inactive'} />
    </div>
  );
}

export default function LicenseStatusSection() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subData, setSubData] = useState(null);
  const [idToken, setIdToken] = useState('');
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user) {
        setSubData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(false);
      const token = await getIdToken();
      if (cancelled) return;
      setIdToken(token || '');

      const data = await fetchSubscriptionDetails();
      if (cancelled) return;
      setSubData(data);
      setLoadError(!data);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const summary = getSearchLicenseSummary(subData);
  const upgradeUrl = getKiddowareAccountUrl(idToken);

  const openUpgrade = () => {
    trackUserAction.externalLink('kiddoware_account_upgrade');
    window.open(upgradeUrl, '_blank', 'noopener,noreferrer');
  };

  if (!user) {
    return (
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={18} aria-hidden />
          License &amp; web search
        </h3>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, opacity: 0.85 }}>
          Sign in to see whether your account includes supervised web search (
          {SEARCH_LICENSE_PRODUCT_LABELS.kw} or {SEARCH_LICENSE_PRODUCT_LABELS.kpsb}).
        </p>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Shield size={18} aria-hidden style={{ color: 'var(--color-primary)' }} />
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--color-text)' }}>
          License &amp; web search
        </h3>
      </div>

      <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.5, opacity: 0.78 }}>
        {SEARCH_LICENSE_UPGRADE_HINT} Only these plans include search:
      </p>

      {loading ? (
        <p style={{ margin: 0, fontSize: 13, opacity: 0.7 }}>Loading license status…</p>
      ) : loadError ? (
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#b91c1c' }}>
          Could not load license status. Try again later or open your Kiddoware account.
        </p>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-card)',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Search size={15} aria-hidden />
              Web search
            </span>
            <StatusPill
              active={summary.searchEnabled}
              label={summary.searchEnabled ? 'Included' : 'Not included'}
            />
          </div>

          <ProductLicenseRow product={summary.kw} />
          <ProductLicenseRow product={summary.kpsb} />

          <p style={{ margin: '12px 0 0', fontSize: 12, lineHeight: 1.45, opacity: 0.72 }}>
            {summary.headline}
          </p>
        </>
      )}

      <button
        type="button"
        onClick={openUpgrade}
        style={{
          marginTop: 16,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 18px',
          borderRadius: 10,
          border: 'none',
          background: 'var(--color-primary)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 14,
          cursor: 'pointer',
          boxShadow: '0 2px 10px var(--color-shadow)',
        }}
      >
        {summary.searchEnabled ? 'Manage subscription' : 'Upgrade at app.kiddoware.com'}
        <ExternalLink size={16} aria-hidden />
      </button>
    </div>
  );
}
